import classNames from "classnames";
import { useLocation } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { useCurrentPostContext } from "@/react/contexts/currentPostContext";
import { isPostDetailsPage } from "@circle-react/helpers/isPostDetailsPage";
import { toLocaleString } from "@circle-react/helpers/number";
import { SkeletonLoader } from "@circle-react-uikit/SkeletonLoader";
import { useNavigateToPost } from "../PostModal/hooks/useNavigateToPost";
import { usePostNavigationContext } from "../PostViewList/PostNavigationContext";
import { useCurrentPostHelpers } from "../useCurrentPostHelpers";

interface CommentCountLinkProps {
  count: number;
  hasPostDetailsLoaded: boolean;
  shouldForceSamePageInteractions?: boolean;
  toggleComment: () => void;
}

export const CommentCountLink = ({
  count,
  toggleComment,
  hasPostDetailsLoaded,
  shouldForceSamePageInteractions = false,
}: CommentCountLinkProps) => {
  const { post } = useCurrentPostHelpers();
  const { usedIn } = useCurrentPostContext();
  const location = useLocation();
  const { navigateToPost } = useNavigateToPost();
  const { params, hasNextPage } = usePostNavigationContext();

  /*
   * There's an edge case where the user has the post details page open and
   * they open the profile modal and navigate to the "Posts" tab. If they then
   * click the comment count link in one of the posts within the modal, we must
   * open a nested post modal to avoid issues with the infinite scroll. Otherwise,
   * loading comments for posts that are not at the bottom of the list won't work,
   * given the scrolling target is the whole modal and not the focused comments list.
   */
  const shouldShowPostModal =
    !isPostDetailsPage() || usedIn === "profile-modal";

  if (!hasPostDetailsLoaded) {
    return <SkeletonLoader variant="row" lg long />;
  }

  return (
    <button
      type="button"
      className={classNames(
        "hover:!text-dark focus-visible:outline-secondary text-default p-0 text-sm font-medium transition-colors duration-150 ease-in-out focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
      )}
      data-testid="post-comment-count"
      onClick={() => {
        // eslint-disable-next-line sonarjs/no-selector-parameter -- Disabled for plugin version update. Fix when possible
        shouldShowPostModal && !shouldForceSamePageInteractions
          ? navigateToPost({
              newPost: post,
              prevLocation: location,
              params,
              hasNextPage,
              shouldFocusOnCommentsSection: true,
            })
          : toggleComment();
      }}
    >
      {t("post.comments", { count, formatted_count: toLocaleString(count) })}
    </button>
  );
};
